import type { ReactNode } from 'react'
import React, { createContext, useContext, useMemo, useState } from 'react'

import { SuspenseFallback } from '@fourthwall/components'

import { useShopContext } from './ShopProvider'

type Props = {
  children: ReactNode
}

type ActiveTheme = {
  id: string
  name: string
}

export type ActiveThemeContextType = {
  activeTheme: ActiveTheme
  isMainTheme: () => boolean
  setActiveTheme: ({ id, name }: ActiveTheme) => void
}

export const ActiveThemeContext = createContext<ActiveThemeContextType | undefined>(undefined)

export const ActiveThemeProvider = ({ children }: Props) => {
  const { shop } = useShopContext()

  // TODO - update schema
  if (!shop.mainThemeId) {
    return <SuspenseFallback />
  }

  const [activeTheme, setActiveTheme] = useState<ActiveTheme>({
    id: shop.mainThemeId,
    name: 'Main',
  })

  const isMainTheme = () => {
    return activeTheme.id === shop.mainThemeId
  }

  const value = useMemo(
    () => ({ activeTheme, isMainTheme, setActiveTheme }),
    [activeTheme, shop.mainThemeId],
  )

  return <ActiveThemeContext.Provider value={value}>{children}</ActiveThemeContext.Provider>
}

export const useActiveThemeContext = () => {
  const activeThemeContext = useContext(ActiveThemeContext)

  if (activeThemeContext === undefined) {
    throw new Error('activeThemeContext must be used within a ActiveThemeProvider')
  }

  return activeThemeContext
}
